import React from 'react';
import {IV_DOCUMENT_IMAGE_TYPES, IDENTITY_VERIFICATION_TYPES} from '@chekin/core';
import {getDocumentType} from '../../../store/selectors/getDocumentType';
import {useIVStore} from '../../../store/useIVStore';
import {useIVComputedDetails} from '../useIVComputedDetails';

export type IVFlowPaths = {
  selfie: string;
  back: string;
  done: string;
};

type Props = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  onlyScanning?: boolean;
  paths: IVFlowPaths;
};

function useGetNextStepAfterIV({docType, onlyScanning, paths}: Props) {
  const documentType = useIVStore(getDocumentType);
  const {biomatch} = useIVComputedDetails();

  const getNextStepDetails = React.useCallback(() => {
    if (onlyScanning) {
      return {
        path: paths.done,
        type: IDENTITY_VERIFICATION_TYPES.ocr,
        side: IV_DOCUMENT_IMAGE_TYPES.back,
        completed: true,
      };
    }

    const isIvBypassed = useIVStore.getState().declaredIvBypassAge != null;

    switch (docType) {
      case IV_DOCUMENT_IMAGE_TYPES.front:
        if (isIvBypassed) {
          return {
            path: paths.done,
            type: IDENTITY_VERIFICATION_TYPES.ocr,
            side: IV_DOCUMENT_IMAGE_TYPES.front,
            completed: true,
          };
        }
        if (documentType?.two_sides) {
          return {
            path: paths.back,
            side: IV_DOCUMENT_IMAGE_TYPES.front,
            completed: false,
          };
        }
        if (biomatch) {
          return {
            path: paths.selfie,
            type: IDENTITY_VERIFICATION_TYPES.biomatch,
            side: IV_DOCUMENT_IMAGE_TYPES.front,
            completed: false,
          };
        }
        return {
          path: paths.done,
          type: IDENTITY_VERIFICATION_TYPES.ocr,
          side: IV_DOCUMENT_IMAGE_TYPES.front,
          completed: true,
        };
      case IV_DOCUMENT_IMAGE_TYPES.back:
        if (isIvBypassed) {
          return {
            path: paths.done,
            type: IDENTITY_VERIFICATION_TYPES.ocr,
            side: IV_DOCUMENT_IMAGE_TYPES.back,
            completed: true,
          };
        }
        if (biomatch) {
          return {
            path: paths.selfie,
            type: IDENTITY_VERIFICATION_TYPES.biomatch,
            side: IV_DOCUMENT_IMAGE_TYPES.back,
            completed: false,
          };
        }
        return {
          path: paths.done,
          type: IDENTITY_VERIFICATION_TYPES.ocr,
          side: IV_DOCUMENT_IMAGE_TYPES.back,
          completed: true,
        };
      case IV_DOCUMENT_IMAGE_TYPES.selfie:
        return {
          path: paths.done,
          type: IDENTITY_VERIFICATION_TYPES.biomatch,
          side: IV_DOCUMENT_IMAGE_TYPES.selfie,
          completed: true,
        };
      default:
        return {path: paths.done, completed: true};
    }
  }, [onlyScanning, biomatch, docType, documentType?.two_sides, paths]);

  return {getNextStepDetails};
}

export {useGetNextStepAfterIV};
